import { computed, observable, runInAction } from "mobx"

import { Model } from "@/lib/Model.ts"

import type { TD_MemberProfile, TypingEventDTO } from "@/lib/client/generated/TypeDefinitions.ts"
import type { Queries } from "@/lib/queries.ts"
import type { IObservableValue } from "mobx"

type TypingUser = {
  profile: TD_MemberProfile
  timestamp: number
}

const TYPING_TIMEOUT = 3000

export class TypingIndicatorModel extends Model {
  private readonly typingUsers = observable.map<string, TypingUser>()
  private interval: NodeJS.Timeout | null = null

  readonly currentlyTyping: IObservableValue<TD_MemberProfile[]> = computed(() => {
    const now = Date.now()
    const typing: TD_MemberProfile[] = []

    for (const user of this.typingUsers.values()) {
      if (now - user.timestamp < TYPING_TIMEOUT) {
        typing.push(user.profile)
      }
    }

    return typing
  })

  constructor(
    taskId: string,
    queries: Queries
  ) {
    super("TypingIndicatorModel")

    this.lifetime.add(queries.subscribeToTypingEvents(taskId).subscribe(this.consumeTypingEvent), `subscribeToTypingEvents::${taskId}`)
    this.lifetime.add(() => this.interval !== null && clearInterval(this.interval), "TypingIndicatorModel:cleanup")
  }

  private consumeTypingEvent = (event: TypingEventDTO) => {
    if (event.principal.details?.className == "CUserPrincipalDetails") {
      const profile = event.principal.details.user
      if (event.stop) {
        runInAction(() => {
          this.typingUsers.delete(profile.id)
        })
      } else {
        runInAction(() => {
          this.typingUsers.set(profile.id, {
            profile,
            timestamp: Date.now()
          })
          this.restartIntervalIfNeeded()
        })
      }
    }
  }

  private restartIntervalIfNeeded = () => {
    if (this.typingUsers.size > 0 && this.interval == null) {
      this.interval = setInterval(() => {
        this.cleanupExpiredTypingUsers()
        if (this.typingUsers.size == 0 && this.interval !== null) {
          clearInterval(this.interval)
          this.interval = null
        }
      }, 1000)
    }
  }

  private cleanupExpiredTypingUsers(): void {
    const now = Date.now()
    const expiredUsers: string[] = []

    for (const [profileId, user] of this.typingUsers.entries()) {
      if (now - user.timestamp >= TYPING_TIMEOUT) {
        expiredUsers.push(profileId)
      }
    }

    if (expiredUsers.length > 0) {
      runInAction(() => {
        for (const profileId of expiredUsers) {
          this.typingUsers.delete(profileId)
        }
      })
    }
  }
}